/**
 * v6 migration notes:
 *
 *  - `useHistory` -> `useNavigate`; `useLocation` is unaffected by v5->v6
 *    (same hook, same shape).
 *
 *  - `history.replace('/')` -> `navigate('/', { replace: true })`.
 *
 *  - `history.replace({ ...location, state: null })` is the trickiest one
 *    here. v5 let you pass a location-shaped object to replace() to "stay on
 *    the same URL but clear router state" (so a page refresh after payment
 *    wouldn't re-read stale location.state and re-attempt the flow). v6's
 *    navigate() doesn't take a raw location object the same way — the
 *    equivalent is navigating to the current path again with
 *    `{ replace: true, state: null }` as the options object. Fixed below as
 *    `navigate(location.pathname + location.search, { replace: true, state: null })`,
 *    used both after a completed payment and after a promo code apply.
 *
 *  - PRE-EXISTING BUG, surfaced by reasoning through the v6 rewrite (not
 *    introduced by it): this file reads `location.state.paymentType`
 *    directly (no `?.`) in the render — twice, on the save-card checkbox.
 *    On a direct visit to `/payment` with no router state at all (e.g. a
 *    bookmarked URL, or a page refresh before the redirect effect runs),
 *    `location.state` is `null` on the very first render, and that render
 *    happens *before* the `!isAuthorized` redirect effect has a chance to
 *    fire — so this line throws `Cannot read properties of null (reading
 *    'paymentType')` and crashes the page instead of cleanly redirecting.
 *    (The post-payment `navigate(..., { state: null })` call in `finally`
 *    does NOT hit this, since `paymentStatus` is already set by then and
 *    the component renders the `<PaymentStatus>` branch instead — verified
 *    by tracing the render order.) Fixed by reading via `location.state?.`
 *    everywhere, and by gating the two `paymentType` checkbox reads behind
 *    a single `paymentType` local variable so a null state can't crash the
 *    render on that first frame.
 *
 *  - This also CONFIRMS the fix made in MembershipPlans.js was both correct
 *    and necessary: this file expects `location.state` to be shaped exactly
 *    like `{ isAuthorized, product, paymentType }` — which only arrives
 *    correctly if the caller uses v6's `navigate(path, { state: {...} })`
 *    wrapper. Without that fix, `location.state` would always have been
 *    `null` here and every visit to this page would immediately bounce to
 *    `/` via the `!isAuthorized` check.
 */
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import React, { useState, useEffect } from 'react';
import { useNavigate,  useLocation } from 'react-router-dom';
import DropIn from 'braintree-web-drop-in-react';
import styles from './BrainTreePaymentsPage.module.scss';
import { connect } from 'react-redux';

import { AppWrapper, Button, Spinner } from 'components';
import OrderSummary from 'components/OrderSummary/OrderSummary';
import PaymentStatus from 'components/PaymentStatus/PaymentStatus';
import SavedCardItem from 'components/SavedCardItem/SavedCardItem';
import AuthProtected from 'containers/AuthProtected/AuthProtected';
import { PAYMENT_TYPES } from 'coupon-common';
import { actions as plansActions } from 'coupon-common/src/modules/memberships/plans';
import { actions as paymentActions } from 'coupon-common/src/modules/payment';
import { actions as taxeRateActions } from 'coupon-common/src/modules/taxRates';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import HeaderLogo from '../../components/Logo/HeaderLogo.svg';
import messages from './messages';

// ─── Helper FN ─────────────────────────────────────────────────────────────

const buildPayload = ({ nonce, product, businessInfo, saveCardDetails, start_trial, paymentType, selectedCard, promoCode }) => ({
    ...(nonce ? { nonce } : {}),
    product_or_mem_id: product?.mem_id || product?.id,
    first_name: businessInfo?.name,
    email: businessInfo?.email,
    saveCardDetails: paymentType === PAYMENT_TYPES.SUBSCRIPTION ? true : saveCardDetails,
    start_trial,
    ...(selectedCard ? { saved_payment_method: selectedCard } : {}),
    ...(promoCode ? { promo_code: promoCode } : {}),
});

// ─── Main Component ───────────────────────────────────────────────────────────

const BrainTreePaymentsPage = ({ intl, membershipPlans, businessInfo, savedCards, taxes, dispatch }) => {
    const [clientToken, setClientToken] = useState('');
    const [braintreeInstance, setBraintreeInstance] = useState(null);
    const [loadingDropIn, setLoadingDropIn] = useState(true);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [product, setProduct] = useState(null);
    const [orderSummary, setOrderSummary] = useState({ price: 0, taxes: 0, promo: null, total: 0 });
    const [payNowEnabled, setPayNowEnabled] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [saveCardDetails, setSaveCardDetails] = useState(false);

    // Saved card state
    const [selectedCard, setSelectedCard] = useState(null);
    const [useNewCard, setUseNewCard] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Read defensively — location.state is null right after the post-payment
    // clear-state navigate() call below, and on any direct visit to this
    // route with no router state at all (see header note).
    const paymentType = location.state?.paymentType;
    const hasSavedCards = savedCards && savedCards?.length > 0;

    const oneTimeProductHandlers = {
        coupon_pack: async ({ orderId, dispatch }) => {
            await dispatchAsAsync(
                paymentActions.confirmCouponPack(orderId),
                dispatch
            );
        },

        ad_boost: async ({ orderId, dispatch }) => {
            await dispatchAsAsync(
                paymentActions.confirmAdBoost(orderId),
                dispatch
            );
        },

        default: async ({ orderId, dispatch }) => {
            await dispatchAsAsync(
                paymentActions.confirmOneTimePurchase(orderId),
                dispatch
            );
        },
    };

    // Promocode states
    const [promoCode, setPromoCode] = useState('');
    const [appliedPromoCode, setAppliedPromoCode] = useState(null); // locked-in after Apply
    const [promoResult, setPromoResult] = useState(null);  // { valid, discounted_amount, you_save, error }
    const [promoLoading, setPromoLoading] = useState(false);

    // If there are saved cards and user hasn't chosen "new card", pay button
    // is enabled as soon as a card is selected (no Drop-In interaction needed).
    const isSavedCardMode = hasSavedCards && !useNewCard;
    const isPayEnabled = isSavedCardMode
        ? !!selectedCard
        : payNowEnabled;

    // ── Effects ────────────────────────────────────────────────────────────

    useEffect(() => {
        dispatch(paymentActions.getPaymentMethods());
        if (membershipPlans.length === 0) {
            dispatch(plansActions.getMembershipPlans());
        }
    }, [membershipPlans, dispatch]);

    useEffect(() => {
        const isAuthorized = location.state?.isAuthorized;
        const productInfo = location.state?.product;

        if (!isAuthorized || !productInfo) {
            navigate('/', { replace: true });
            return;
        }

        setProduct(productInfo);

        const price = Number(productInfo.price);

        // ✅ Call API
        dispatch(taxeRateActions.getTaxRates({ price }));

        fetchClientToken();
    }, []);

    useEffect(() => {
        if (!product || !taxes || !taxes.total_tax) return;

        const price = Number(product.price);
        const taxAmount = Number(taxes.total_tax);
        const total = (price - orderSummary?.promo) + taxAmount;

        setOrderSummary((prev) => ({
            ...prev,
            price: price.toFixed(2),
            taxes: taxAmount.toFixed(2),
            total: total.toFixed(2),
        }));
    }, [taxes, product]);

    // Pre-select the first saved card when cards load
    useEffect(() => {
        if (hasSavedCards && !selectedCard) {
            const defaultCard = savedCards?.find(card => card.is_default) || savedCards[0];
            setSelectedCard(defaultCard);
        }
    }, [savedCards]);

    // ── Fetch client token ──────────────────────────────────────────────────

    const fetchClientToken = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}payment/get-client-token/`);
            const data = await response.json();
            setClientToken(data.clientToken);
        } catch (error) {
            console.error('Error fetching client token:', error);
        }
    };

    // ── Payment handler ─────────────────────────────────────────────────────

    const handlePayment = async () => {
        if (!isSavedCardMode && !braintreeInstance) return;
        setPaymentLoading(true);

        try {
            const start_trial = location?.state?.product?.start_trial || false;

            // Saved-card path: backend resolves the payment method from
            // last4_digits alone — no nonce needed or expected.
            // New-card path: get a nonce from the Drop-In as usual.
            const nonce = isSavedCardMode
                ? null
                : (await braintreeInstance.requestPaymentMethod()).nonce;

            const payload = buildPayload({
                nonce,
                product,
                businessInfo,
                saveCardDetails,
                start_trial,
                paymentType,
                selectedCard: isSavedCardMode ? selectedCard : null,
                promoCode: appliedPromoCode
            });

            const action =
                paymentType === PAYMENT_TYPES.SUBSCRIPTION
                    ? paymentActions.subscribe
                    : paymentActions.checkout;

            const data = await dispatchAsAsync(action(payload), dispatch);

            if (data.success) {
                setPaymentStatus({ status: 'success', data: { ...data, free_trial: start_trial } });
            } else {
                setPaymentStatus({ status: 'error', error: data.error || 'Unknown error occurred. Please try again' });
            }
        } catch (err) {
            setPaymentStatus({ status: 'error', error: (err || "Internal server error. Please try again.") });
        } finally {
            setPaymentLoading(false);
            // Clear location state so a refresh doesn't re-read stale
            // isAuthorized/product/paymentType and re-attempt the flow.
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
    };

    // ── Handlers ────────────────────────────────────────────────────────────

    const handleSelectCard = (card) => setSelectedCard(card);

    const handleUseNewCard = () => {
        setUseNewCard(true);
        setSelectedCard(null);
        setPayNowEnabled(false);
    };

    const handleBackToSavedCards = () => {
        setUseNewCard(false);
        if (hasSavedCards) setSelectedCard(savedCards[0]);
    };

    const handleApplyPromo = async () => {
        try {
            setPromoLoading(true);

            const data = await dispatchAsAsync(paymentActions.validatePromoCode({ code: promoCode, plan_id: product?.mem_id }), dispatch);

            if (data?.valid) {
                const originalPrice = Number(orderSummary?.price);
                let discount = 0;

                if (data?.discount_percent) {
                    discount = (originalPrice * data.discount_percent) / 100;
                } else if (data?.discount_amount) {
                    discount = Number(data.discount_amount);
                }

                const discountedPrice = Math.max(originalPrice - discount, 0).toFixed(2);
                const discountFormatted = discount.toFixed(2);

                setAppliedPromoCode(promoCode);
                setPromoResult({
                    valid: data?.valid,
                    discount_amount: data?.discount_amount,
                    discount_percent: data?.discount_percent,
                    original_amount: originalPrice,
                    discounted_amount: discountedPrice,
                    you_save: discountFormatted,
                });

                dispatch(taxeRateActions.getTaxRates({ price: discountedPrice }));
                setOrderSummary((prev) => ({ ...prev, promo: discountFormatted }));
            } else {
                const error = (data.error) || "Invalid promo code.";
                setPromoResult({ valid: false, error });
            }
        } catch (err) {
            const error = (err.response && err.response.data && err.response.data.error) || "Invalid promo code.";
            setPromoResult({ valid: false, error });
        } finally {
            setPromoLoading(false);
        }
    };

    const handleRemovePromo = () => {
        setPromoCode('');
        setAppliedPromoCode(null);
        setPromoResult(null);
        // re-fetch taxes without discount
        dispatch(taxeRateActions.getTaxRates({ price: Number(product.price) }));
        setOrderSummary((prev) => ({ ...prev, promo: null }));
    };

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <AppWrapper showHeader contentCentered>
            <AuthProtected />
            {!clientToken ? (
                <Spinner show={loadingDropIn} />
            ) : (
                <div className={`${styles.pageWrapper} ${paymentLoading ? styles.disabled : ''}`}>
                    {paymentStatus ? (
                        <PaymentStatus paymentStatus={paymentStatus} />
                    ) : (
                        <div className={styles.checkoutCard}>
                            {/* Logo */}
                            <div className={styles.logoWrapper}>
                                <img
                                    src={HeaderLogo}
                                    alt="logo"
                                    className={styles.logo}
                                />
                            </div>

                            <h2 className={styles.title}>{intl.formatMessage(messages.checkoutTitle)}</h2>
                            <p className={styles.subtitle}>{intl.formatMessage(messages.checkoutSubtitle)}</p>

                            <OrderSummary product={product} orderSummary={orderSummary} />

                            <div className={styles.promoWrapper}>
                                {appliedPromoCode ? (
                                    <div className={styles.appliedPromo}>
                                        <span>🏷 <strong>{appliedPromoCode}</strong> applied</span>
                                        <button onClick={handleRemovePromo} className={styles.removePromo}>✕ Remove</button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            placeholder="Enter promo code"
                                            value={promoCode}
                                            onChange={(e) => setPromoCode(e.target.value)}
                                            className={styles.promoInput}
                                        />
                                        <Button
                                            onClick={handleApplyPromo}
                                            disabled={!promoCode || promoLoading}
                                            className={styles.promoButton}
                                        >
                                            {promoLoading ? 'Applying...' : 'Apply'}
                                        </Button>
                                    </>
                                )}
                                {promoResult?.valid && (
                                    <p className={styles.promoSuccess}>You save ${promoResult.you_save}!</p>
                                )}
                                {promoResult && !promoResult.valid && (
                                    <p className={styles.promoError}>{promoResult.error}</p>
                                )}
                            </div>

                            {/* ── Payment method section ── */}
                            {hasSavedCards && !useNewCard ? (
                                <>
                                    {/* Saved cards list */}
                                    <div className={styles.savedCardsSection}>
                                        <h5 className={styles.sectionLabel}>{intl.formatMessage(messages.savedCardsSectionLabel)}</h5>

                                        {savedCards.map((card) => (
                                            <SavedCardItem
                                                key={card?.payment_token}
                                                card={card}
                                                isSelected={selectedCard?.payment_token === card?.payment_token}
                                                onSelect={handleSelectCard}
                                            />
                                        ))}
                                    </div>

                                    {/* Option to use a new card */}
                                    <button
                                        className={styles.useNewCardButton}
                                        onClick={handleUseNewCard}
                                    >
                                        {intl.formatMessage(messages.useNewCardButton)}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {/* Back to saved cards link */}
                                    {hasSavedCards && (
                                        <button
                                            className={styles.backToSavedButton}
                                            onClick={handleBackToSavedCards}
                                        >
                                            {intl.formatMessage(messages.backToSavedCardsButton)}
                                        </button>
                                    )}

                                    {/* Braintree Drop-In for manual entry */}
                                    <DropIn
                                        options={{
                                            authorization: clientToken,
                                            card: {
                                                cardholderName: {
                                                    required: true
                                                },
                                            }
                                        }}
                                        className={styles.dropInWrapper}
                                        onInstance={(instance) => {
                                            setBraintreeInstance(instance);
                                            setLoadingDropIn(false);
                                            instance.on('paymentMethodRequestable', () => setPayNowEnabled(true));
                                            instance.on('noPaymentMethodRequestable', () => setPayNowEnabled(false));
                                        }}
                                    />

                                    {/* Save card checkbox */}
                                    <div className={styles.saveCardWrapper}>
                                        <label className={styles.saveCardLabel}>
                                            <input
                                                type="checkbox"
                                                checked={saveCardDetails || paymentType === PAYMENT_TYPES.SUBSCRIPTION}
                                                disabled={paymentType === PAYMENT_TYPES.SUBSCRIPTION}
                                                onChange={(e) => setSaveCardDetails(e.target.checked)}
                                            />
                                            <span>{intl.formatMessage(messages.saveCardLabel)}</span>
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* Pay button */}
                            <div className={styles.paymentSummaryWrapper}>
                                <div className={styles.paymentSummary}>
                                    <span>{intl.formatMessage(messages.totalPriceLabel)}</span>
                                    <strong>${orderSummary.total}</strong>
                                </div>
                                <Button
                                    onClick={handlePayment}
                                    primary
                                    className={styles.payButton}
                                    disabled={!isPayEnabled || paymentLoading}
                                    fetching={paymentLoading}
                                >
                                    {intl.formatMessage(messages.payNowButton)}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </AppWrapper>
    );
};

BrainTreePaymentsPage.propTypes = {
    intl: PropTypes.object.isRequired,
    dispatch: PropTypes.func,
    membershipPlans: PropTypes.array,
    savedCards: PropTypes.array,
    businessInfo: PropTypes.object,
    taxes: PropTypes.object,
};

const mapStateToProps = (state) => ({
    membershipPlans: state.memberships.plans.data || [],
    businessInfo: state.businessInfo.data || {},
    savedCards: state.payment.paymentMethods || [],
    taxes: state.taxRates.data || {},
});

export default connect(mapStateToProps)(injectIntl(BrainTreePaymentsPage));